export interface ApiError extends Error {
  status: number;
  requiresAuth?: boolean;
}

let csrfToken: string | null = null;

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const CSRF_EXEMPT = new Set([
  '/auth/login',
  '/auth/register',
  '/auth/register/complete',
  '/auth/forgot-password',
  '/auth/reset-password',
]);

async function getCsrfToken(): Promise<string | null> {
  if (csrfToken) return csrfToken;

  const res = await fetch('/api/auth/csrf', { credentials: 'include' });
  if (!res.ok) return null;

  const payload = (await res.json().catch(() => null)) as { csrfToken?: string } | null;
  csrfToken = typeof payload?.csrfToken === 'string' ? payload.csrfToken : null;
  return csrfToken;
}

/**
 * Fired when the server refuses a request for want of standing (401/403).
 * The session provider listens and re-reads /auth/me, so a grant withdrawn
 * while the page was open is reflected in the sidebar and route guards
 * without anyone having to reload.
 */
export const AUTH_STALE_EVENT = 'fttmy:auth-stale';

/** Thin fetch wrapper: always sends the session cookie, always throws typed errors. */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  if (!(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (UNSAFE.has(method) && !CSRF_EXEMPT.has(path)) {
    const token = await getCsrfToken();
    if (token) headers.set('X-CSRF-Token', token);
  }

  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    ...init,
    headers,
  });

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    const error = new Error(
      payload?.error ?? `Request failed (${res.status})`,
    ) as ApiError;
    error.status = res.status;
    error.requiresAuth = payload?.requiresAuth ?? res.status === 401;
    if (res.status === 401 || res.status === 403) {
      csrfToken = null;
      window.dispatchEvent(new Event(AUTH_STALE_EVENT));
    }
    throw error;
  }
  if (path === '/auth/login' || path === '/auth/logout' || path === '/auth/change-password') {
    csrfToken = null;
  }
  return payload as T;
}

/* ------------------------------- API types -------------------------------- */

export type Role = 'tech_superadmin' | 'super_admin' | 'admin' | 'user' | 'church';

/*
  Where each role sits. Mirrors the server: an account may only change, delete
  or hand out roles at or below its own rank, so a super admin never reaches a
  tech admin and an admin never reaches a super admin.
*/
export const ROLE_RANK: Record<Role, number> = {
  tech_superadmin: 3,
  super_admin: 2,
  admin: 1,
  user: 0,
  church: 0,
};

/** Whether an account holding `own` may manage an account holding (or be given) `target`. */
export const reaches = (own: Role, target: Role): boolean => ROLE_RANK[own] >= ROLE_RANK[target];

/** Tech and super admins decide which areas an admin is let into. */
export const grantsAreas = (role: Role | undefined): boolean =>
  role === 'tech_superadmin' || role === 'super_admin';

/*
  A trainee's record as the application form gave it is changed only by a
  super admin or above; an admin keeps the Currently part up to date. Mirrors
  the server, which refuses the rest.
*/
export const editsTraineeRecords = (role: Role | undefined): boolean =>
  role !== undefined && ROLE_RANK[role] >= ROLE_RANK.super_admin;
export type Visibility = 'public' | 'user' | 'church' | 'staff';

/*
  Areas an admin is let into one at a time. 'users' is account management;
  'info' is the trainee register and intakes. Tech and super admins hold both
  without being asked; the other roles can hold neither.
*/
export const PERMISSIONS = ['users', 'info'] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const PERMISSION_LABELS: Record<Permission, { label: string; hint: string }> = {
  users: { label: 'Users', hint: 'Approve, create, edit and delete accounts.' },
  info: { label: 'Info', hint: 'Trainees and intakes — view, add, edit and delete.' },
};

export interface User {
  id: number;
  email: string;
  fullName: string;
  role: Role;
  permissions: Permission[];
}

export interface NavPage {
  slug: string;
  title: string;
  navGroup: string | null;
  sortOrder: number;
}

export interface Page extends NavPage {
  id: number;
  excerpt: string;
  contentHtml: string;
  visibility: Visibility;
  seoTitle: string | null;
  seoDescription: string | null;
  updatedAt: string;
  heroUrl: string | null;
  heroAlt: string | null;
}

export interface Announcement {
  isActive: boolean;
  title: string;
  date: string;
  time: string;
  venue: string;
  /** Optional free-text note shown under the details. */
  bodyHtml: string;
}

export interface SiteSettings {
  site?: { name: string; shortName: string; tagline: string };
  contact?: { phone: string[]; email: string; address: string };
  homepage_announcement?: Announcement;
  /*
    Who approval notices are addressed to. Internal, so it comes back from
    /admin/settings only — the public /settings endpoint never includes it.
  */
  notification_emails?: { to: string[]; cc: string[] };
  /** Whether new people may ask for an account. Closed while missing. */
  registration?: { open: boolean };
}

export const ROLE_LABELS: Record<Role, string> = {
  tech_superadmin: 'Tech Admin',
  super_admin: 'Super Admin',
  admin: 'Admin',
  user: 'User',
  church: 'Church',
};

export const isStaff = (role: Role | undefined): boolean =>
  grantsAreas(role) || role === 'admin';

/** Whether this account may enter the given area. Mirrors the server's rule. */
export const can = (user: User | null | undefined, permission: Permission): boolean =>
  !!user &&
  (grantsAreas(user.role) ||
    (user.role === 'admin' && user.permissions.includes(permission)));
